import { useContext, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { GeaoContext } from "../../../GeaoContextProvider";
import RadioButtonGroup from "../../../../gds/RadioButtonGroup";
import useCobrandedAgency from "../../../hooks/useCobrandedAgency";

/**
 * AgencyCommissionPayments component renders a form field to determine
 * whether an agency will receive commission payments directly from GEICO
 * or if a parent agency manages the commission payments.
 * 
 * This component is conditionally rendered based on the agency's affiliation
 * with Top 100 firms, network, or aggregator.
 * 
 * For cobranded agencies, this question is pre-filled (default to No) and disabled.
 * 
 * @component
 * @returns {JSX.Element} A fieldset containing radio buttons for commission payment options
 */
export default function AgencyCommissionPayments() {
    const { agencyInterview } = useContext(GeaoContext);
    const { isCobrandedAgency } = useCobrandedAgency();

    const legend = <>
        Will {agencyInterview?.questionAnswers?.AgencyName?.answer ?? "your agency"} receive commissions payments directly from GEICO?{/*
        */}<span className='required'>*</span>
    </>;

    /**
     * Radio button options for commission payment preferences
     * @type {Array<{value: boolean, label: string}>}
     */
    const options = [
        {
            value: "true",
            trackingId: "Yes",
            label:
                `Yes - ${agencyInterview?.questionAnswers?.AgencyName?.answer ?? "our agency"} ` +
                "should receive commission payments directly from GEICO."
        },
        {
            value: "false",
            trackingId: "No",
            label:
                `No - a parent agency manages ${agencyInterview?.questionAnswers?.AgencyName?.answer ?? "our agency"} ` +
                "commission payments."
        }
    ];

    const { control, setValue, watch } = useFormContext();

    // Watch form values to determine conditional rendering
    const watchAffiliatedWithTop100 = watch("affiliatedWithTop100");
    const watchFirmNetworkOrAggregator = watch("firmNetworkOrAggregator");
    const watchFirmNetworkAggregatorCommissionPayments = watch("firmNetworkAggregatorCommissionPayments");

    /**
     * Determines whether to show the agency commission payments question
     * based on the agency's affiliation status and network settings.
     * 
     * Shows the question when:
     * - Agency is a cobranded sub-agency (pre-filled and disabled), OR
     * - Agency is not affiliated with Top 100 firms, OR
     * - Agency is affiliated with Top 100 but the affiliated firm does NOT manage commission payments
     * 
     * @type {boolean}
     */
    const showAgencyCommissionPayments = useMemo(() => {
        // Always show for cobranded agencies (will be disabled)
        if (isCobrandedAgency) {
            return true;
        }

        if (watchAffiliatedWithTop100 === "false") {
            return true;
        }

        return (
            watchAffiliatedWithTop100 === "true" &&
            watchFirmNetworkOrAggregator &&
            watchFirmNetworkAggregatorCommissionPayments === "false"
        );
    }, [
        isCobrandedAgency,
        watchAffiliatedWithTop100,
        watchFirmNetworkOrAggregator,
        watchFirmNetworkAggregatorCommissionPayments
    ]);

    /**
     * Effect to default agencyCommissionPayments to "false" (No) when the question
     * should not be shown. This indicates the agency does not receive commissions
     * directly from GEICO and prevents stale data from being submitted.
     */
    useEffect(() => {
        if (!showAgencyCommissionPayments && !isCobrandedAgency) {
            setValue("agencyCommissionPayments", "false");
        }
    }, [showAgencyCommissionPayments, isCobrandedAgency, setValue]);

    // Watch the current value to know when it's been set
    const watchAgencyCommissionPayments = watch("agencyCommissionPayments");

    /**
     * Effect to set agencyCommissionPayments to "false" (No) for cobranded agencies.
     * This ensures the disabled field shows the correct pre-selected value.
     */
    useEffect(() => {
        if (isCobrandedAgency && watchAgencyCommissionPayments !== "false") {
            setValue("agencyCommissionPayments", "false");
        }
    }, [isCobrandedAgency, watchAgencyCommissionPayments, setValue]);

    // For cobranded agencies, don't render until the value is properly set to "false"
    if (isCobrandedAgency && watchAgencyCommissionPayments !== "false") {
        return null;
    }

    return <>
        {showAgencyCommissionPayments &&
            <RadioButtonGroup trackingId="PrincipalFlow:AgencyOfficeInformation:AgencyCommissionPayments:ReceiveDirectCommissions"
                key={isCobrandedAgency ? "cobranded-commission" : "standard-commission"}
                control={control}
                name="agencyCommissionPayments"
                legend={legend}
                layout="Two Column"
                options={options}
                disabled={isCobrandedAgency}
                defaultValue={isCobrandedAgency ? "false" : undefined}
            />
        }
    </>;
}